import {DataStatus} from 'platform/components';
import {Show} from 'platform/foundation';
import styled from 'styled-components';

import {useMemo} from 'react';
import {Outlet, useLocation} from 'react-router-dom';

import {useGetCurrentUserInfoQuery, useGetUserMenuPinsQuery} from '@dms/api/customer';
import {MenuItemProps} from '@dms/api/shared';
import {moduleKeys} from '@dms/config';
import {PerformancePanel} from '@dms/dev-settings';
import {MAIN_SIDEBAR_WIDTH, MAIN_TOPBAR_HEIGHT} from '@dms/shared';

import {RequiredTestIdProps} from 'shared/web';

import {AppContainer} from './components/AppContainer';
import {PageNavigationBar} from './components/PageNavigationBar';
import {ShowOnMobileNavigation} from './components/ShowOnMobileNavigation';
import {Sidebar} from './components/Sidebar';
import {TopBar} from './components/TopBar';
import {useIsPageNavigationVisible} from './hooks/useIsPageNavigationVisible';
import {isMenuItemActive} from './utils/isMenuItemActive';

const isDev = process.env.ENV_TYPE === 'dev';

interface LayoutProps extends RequiredTestIdProps {
  menuItems: MenuItemProps[];
  menuItemsById: Record<string, MenuItemProps>;
  createMenuItems: MenuItemProps[];
}

export function Layout(props: LayoutProps) {
  const location = useLocation();
  const isPageNavigationVisible = useIsPageNavigationVisible();

  const {
    data: userInfo,
    isLoading: isUserInfoLoading,
    isError: isUserInfoErrored,
  } = useGetCurrentUserInfoQuery();
  const {isLoading: isPinnedMenuItemsLoading, isError: isPinnedMenuItemsErrored} =
    useGetUserMenuPinsQuery();

  const settingsModule = props.menuItems.find((item) => item.id === moduleKeys.settings);
  const activeModule = useMemo(
    () => props.menuItems.find((menuItem) => isMenuItemActive(location.pathname, menuItem.path)),
    [location.pathname, props.menuItems]
  );

  return (
    <DataStatus
      minHeight="100%"
      isLoading={isPinnedMenuItemsLoading || isUserInfoLoading}
      isError={isPinnedMenuItemsErrored || isUserInfoErrored}
      data={userInfo}
    >
      {({data}) => (
        <LayoutGrid>
          <Sidebar
            iconSize={data.settings.sidebarIconSize}
            menuItems={props.menuItems}
            menuItemsById={props.menuItemsById}
            createMenuItems={props.createMenuItems}
            data-testid="sidebar"
          />

          <Show when={isDev}>
            <PerformancePanel />
          </Show>

          <TopBar
            iconSize={data.settings.sidebarIconSize}
            settingsModule={settingsModule}
            activeModule={activeModule}
            data-testid="header"
          />
          <ShowOnMobileNavigation when={isPageNavigationVisible}>
            <PageNavigationBar data-testid="sub-bar" />
          </ShowOnMobileNavigation>

          <AppContainer>
            <Outlet />
          </AppContainer>
        </LayoutGrid>
      )}
    </DataStatus>
  );
}

const LayoutGrid = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: ${({theme}) => theme.getSize(MAIN_SIDEBAR_WIDTH)} 1fr;
  grid-template-rows: ${({theme}) => theme.getSize(MAIN_TOPBAR_HEIGHT)} 1fr;
`;
